import type { NavigateFunction } from "react-router-dom";

/* A double tap on a back button used to pop two screens (or out of a flow):
   navigate(-1) is asynchronous, so the second tap landed before the first
   had moved anything. Back actions inside this window are dropped. */
const BACK_TAP_LOCK_MS = 500;
let backLockedUntil = 0;

/**
 * Closes the on-screen keyboard by taking focus off whatever field has it.
 * Inside the Android web view the keyboard stays up for as long as a field
 * is focused, and everything is laid out in the space above it.
 */
export function dismissKeyboard() {
  const active = document.activeElement as HTMLElement | null;
  if (active && active !== document.body && typeof active.blur === "function") {
    active.blur();
  }
}

/* True for a field that holds the keyboard while focused. */
const isEditable = (el: EventTarget | null) => {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable || el.tagName === "TEXTAREA") return true;
  return (
    el.tagName === "INPUT" &&
    !/^(button|checkbox|radio|submit|reset|file|range|color)$/i.test(
      (el as HTMLInputElement).type
    )
  );
};

/* A tap on a button takes focus off the field before the button's click
   handler runs, so by then nothing focused says the keyboard was up. When
   a field last lost focus tells that instead. */
let fieldBlurredAt = 0;
document.addEventListener("focusout", (e) => {
  if (isEditable(e.target)) fieldBlurredAt = Date.now();
});
const FIELD_JUST_BLURRED_MS = 500;

/* How long Android takes to slide the keyboard away. */
const KEYBOARD_HIDE_MS = 300;

/* The page's full height at each width (a rotation changes it), i.e. its
   height whenever the keyboard is not shrinking it. */
const fullHeights = new Map<number, number>();
const rememberFullHeight = () => {
  const width = window.innerWidth;
  fullHeights.set(
    width,
    Math.max(fullHeights.get(width) ?? 0, window.innerHeight)
  );
};
rememberFullHeight();
window.addEventListener("resize", rememberFullHeight);

/* A page this much shorter than its full height has the keyboard taking
   the rest. Smaller differences are browser chrome, not a keyboard. */
const KEYBOARD_MIN_HEIGHT = 120;

/**
 * Closes the keyboard, then runs `open` once the page is back at full
 * height. For full-screen views (the map) opened from a field that holds
 * the keyboard: where the app shrinks the page for the keyboard, a view
 * opened at once was laid out in the shrunk page — its bottom panel in the
 * middle of the screen — and laid out again when the page grew back, which
 * read as the map reloading. Where the app draws the keyboard over a
 * page it does not shrink, the page cannot see the keyboard go, so `open`
 * waits out its slide instead: opened under it, the view's panel showed
 * above the closing keyboard, mid-screen, and the view's first render held
 * the keyboard up longer. With no keyboard up, `open` runs straight away;
 * `maxWaitMs` caps the wait if no resize ever comes.
 */
export function afterKeyboardCloses(open: () => void, maxWaitMs = 700) {
  const hadKeyboard =
    isEditable(document.activeElement) ||
    Date.now() - fieldBlurredAt < FIELD_JUST_BLURRED_MS;
  dismissKeyboard();
  const full = fullHeights.get(window.innerWidth) ?? window.innerHeight;
  if (window.innerHeight >= full - KEYBOARD_MIN_HEIGHT) {
    if (hadKeyboard) window.setTimeout(open, KEYBOARD_HIDE_MS);
    else open();
    return;
  }
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    window.removeEventListener("resize", onResize);
    window.clearTimeout(timer);
    open();
  };
  const onResize = () => {
    if (window.innerHeight >= full - KEYBOARD_MIN_HEIGHT) finish();
  };
  window.addEventListener("resize", onResize);
  const timer = window.setTimeout(finish, maxWaitMs);
}

/** True when the router has an in-app entry behind the current one. */
export const canGoBack = () => (window.history.state?.idx ?? 0) > 0;

/**
 * Back the way a native screen does: pop to the screen behind when there is
 * one, else REPLACE the current screen with `fallback` (a deep link or a
 * reload has nothing behind it). Pushing `fallback` instead — what the back
 * buttons used to do — left the screen being left on the stack, so the
 * phone's back button then walked "forward" into it again.
 */
export function goBack(navigate: NavigateFunction, fallback: string) {
  const now = Date.now();
  if (now < backLockedUntil) return;
  backLockedUntil = now + BACK_TAP_LOCK_MS;

  if (canGoBack()) navigate(-1);
  else navigate(fallback, { replace: true });
}
